import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'src/redux/store';
import { useWebSocket } from 'src/hooks/useWebSocket';
import { invalidateSubject as invalidateProfileSurvey } from 'src/redux/slices/DataVault/dataVaultProfileSurveySlice';
import { invalidateSubject as invalidateSections } from 'src/redux/slices/DataVault/dataVaultSectionsSlice';
import { invalidateSection } from 'src/redux/slices/DataVault/dataVaultSectionDetailSlice';

interface AnswerUpdatedMsg {
  section_id?: string;
}

export function useProfileWebSocket(targetUserId: number): void {
  const dispatch = useDispatch<AppDispatch>();

  const handleAnswerUpdated = useCallback(
    (data: unknown) => {
      const msg = data as AnswerUpdatedMsg;
      if (msg?.section_id) {
        dispatch(
          invalidateSection({ targetUserId, sectionId: msg.section_id })
        );
      }
      dispatch(invalidateSections(targetUserId));
    },
    [dispatch, targetUserId]
  );

  const handleSectionProgressChanged = useCallback(() => {
    dispatch(invalidateSections(targetUserId));
  }, [dispatch, targetUserId]);

  const handleFileUpdated = useCallback(
    (data: unknown) => {
      const msg = data as AnswerUpdatedMsg;
      if (msg?.section_id) {
        dispatch(
          invalidateSection({ targetUserId, sectionId: msg.section_id })
        );
      }
    },
    [dispatch, targetUserId]
  );

  const handleSurveyUpdated = useCallback(() => {
    dispatch(invalidateProfileSurvey(targetUserId));
  }, [dispatch, targetUserId]);

  useWebSocket({
    enabled: targetUserId > 0,
    url: `profile_user/${targetUserId}/`,
    messageHandlers: {
      'profile_user.answer.updated': handleAnswerUpdated,
      'profile_user.section.progress_changed': handleSectionProgressChanged,
      'profile_user.file.updated': handleFileUpdated,
      'profile_user.survey.updated': handleSurveyUpdated,
    },
    reconnectAttempts: 5,
    reconnectDelay: 3000,
  });
}
